// src/hooks/useSpotlight.js
//
// SPOTLIGHT (Öne çıkanlar şeridi) — Feed üstündeki OneriSlider'ı gerçek
// veriye bağlar.
//
// Veri kaynağı: Firestore `spotlight/{city}` dokümanı (city param, default
// 'milas'). Boşsa `spotlight/_global` fallback. Doküman şekli:
//   { items: [ { type, id?, refId?, title?, username?, name?, uid?,
//                avatar?, cover?, subtitle?, meta?, badge? }, ... ] }
//
// localStorage TTL cache (~6 saat): taze cache varken Firestore'a hiç gidilmez
// (okuma maliyeti + ilk boya için gecikme azalır). useUserPostsCache.js'in
// {items, ts} + TTL deseniyle uyumlu; sadece burada localStorage'a serialize
// edilir (öğeler plain JSON).
//
// Guard: db yoksa, SSR'da, hata olursa → items:[] (sessiz). Component unmount
// sonrası setState yok (mountedRef).

import { useEffect, useRef, useState } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import type { DocumentSnapshot } from 'firebase/firestore';

/**
 * Öne çıkanlar şeridinin ögesi.
 *
 * ⚠️ Alan listesi UYDURULMADI: yukarıdaki başlık yorumunda Firestore doküman
 * şekli zaten YAZILIYDI (`{ type, id?, refId?, title?, … }`) ama YORUM olarak
 * — yani derlenmeyen bir sözleşme. Tüketici `Feed`in beş `handleSpotlight*`
 * gövdesi greplenip alan kümesi doğrulandı; `placeId`/`name`/`link`/`url`/
 * `place`/`toast` yorumda YOKTU ama okunuyor → yorum zaten AYRIŞMIŞTI.
 *
 * 🧭 KANON: *derlenmeyen bir sözleşme, sözleşme değildir.*
 *
 * Alanlar opsiyonel + `[key: string]` açık: doküman elle düzenleniyor ve
 * doğrulanmıyor; tip, gövdenin bildiği kadarını söyler.
 */
export type OneCikanOge = {
  type?: string;
  id?: string;
  refId?: string;
  uid?: string;
  placeId?: string;
  title?: string;
  name?: string;
  placeName?: string;
  username?: string;
  avatar?: string;
  cover?: string;
  subtitle?: string;
  meta?: unknown;
  badge?: unknown;
  link?: string;
  url?: string;
  toast?: string;
  place?: Record<string, unknown>;
  [key: string]: unknown;
};

const TTL_MS = 6 * 60 * 60 * 1000; // ~6 saat
const cacheKey = (city: string) => `spotlight:${city}`;

function readCache(city: string): OneCikanOge[] | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(cacheKey(city));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > TTL_MS) return null;
    return Array.isArray(parsed.items) ? parsed.items : null;
  } catch {
    return null;
  }
}

function writeCache(city: string, items: OneCikanOge[]) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(
      cacheKey(city),
      JSON.stringify({ items: Array.isArray(items) ? items : [], ts: Date.now() }),
    );
  } catch {
    // Quota/private mode — sessiz geç.
  }
}

function extractItems(snap?: DocumentSnapshot | null): OneCikanOge[] | null {
  if (!snap || !snap.exists()) return null;
  const data = snap.data() || {};
  return Array.isArray(data.items) ? data.items : [];
}

/**
 * @param city — Firestore spotlight doc id (default 'milas').
 * (Dönüş şekli için → `OneCikanOge`; JSDoc'ta TEKRAR YAZILMAZ.)
 */
export default function useSpotlight(city = 'milas') {
  const [items, setItems] = useState<OneCikanOge[]>(() => readCache(city) || []);
  // Cache hit varsa loading başlangıçta false (şerit anında dolar).
  const [loading, setLoading] = useState(() => !readCache(city));
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const cached = readCache(city);
    // Stale-while-revalidate: taze cache anında gösterilir AMA yine de
    // arka planda Firestore'dan doğrulanır; içerik değiştiyse güncellenir.
    // (Eski davranış cache varken hiç fetch etmiyordu → seed güncellense bile
    //  6 saat boyunca eski kartlar görünüyordu. Artık her açılışta tazelenir.)
    if (cached) {
      setItems(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    if (!db) {
      if (!cached) {
        setItems([]);
        setLoading(false);
      }
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        let result = extractItems(await getDoc(doc(db, 'spotlight', city)));
        // Şehir dokümanı yok/boşsa global fallback.
        if (!result || result.length === 0) {
          const fallback = extractItems(await getDoc(doc(db, 'spotlight', '_global')));
          if (Array.isArray(fallback)) result = fallback;
        }
        const finalItems = Array.isArray(result) ? result : [];
        writeCache(city, finalItems);
        if (!cancelled && mountedRef.current) {
          // Değişmediyse referansı koru (gereksiz render yok).
          setItems((prev: OneCikanOge[]) =>
            JSON.stringify(prev) === JSON.stringify(finalItems) ? prev : finalItems
          );
          setLoading(false);
        }
      } catch {
        // Sessiz: izin yok / index / offline. Cache yoksa boş şerit.
        if (!cancelled && mountedRef.current) {
          if (!cached) setItems([]);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [city]);

  return { items, loading };
}
